import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { User, Shield, GraduationCap, MapPin, Trophy, Save, Lock } from "lucide-react";
import { ShareConsentCheckbox } from "./ShareConsentCheckbox";

const CENTRAL_IL_LOCATIONS = ["Bloomington", "Normal", "Champaign", "Urbana", "Peoria",
  "Springfield", "Decatur", "Pekin", "Lincoln", "Danville", "Central Illinois (other)"];
const LOCATION_OPTIONS = [...CENTRAL_IL_LOCATIONS, "Outside Central Illinois"];
const CLUB_OPTIONS = ["Peoria FC United", "JGSA", "Morton United FC"];

// Blocking, non-dismissible profile-completion gate. Shows whenever a logged-in
// (non-child) player has NO name set — they cannot reach the Hub/Leaderboard until
// they save Name + Club + School.
export default function ProfileGate() {
  const { player, updateProfile } = usePlayerAuth();

  const [name, setName] = useState(player?.display_name || player?.name || "");
  const [location, setLocation] = useState(player?.location || "");
  const [clubChoice, setClubChoice] = useState("");
  const [clubCustom, setClubCustom] = useState("");
  const [school, setSchool] = useState(player?.school_name || "");
  const [consent, setConsent] = useState(!!player?.share_consent);
  const [saving, setSaving] = useState(false);

  // Only gate real player accounts with no name. Child/sibling profiles always have a name.
  const needsGate = !!player && player.has_access && !player.is_child && !((player.display_name || player.name || "").trim());
  if (!needsGate) return null;

  const clubValue = clubChoice === "Other" ? clubCustom.trim() : clubChoice;
  const leaderboardPreview = (() => {
    const n = (name || "").trim();
    if (!n) return "—";
    const parts = n.split(" ");
    return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
  })();

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Please enter the player's name."); return; }
    if (!clubChoice) { toast.error("Please select your club."); return; }
    if (clubChoice === "Other" && !clubCustom.trim()) { toast.error("Please type your club name."); return; }
    if (!school.trim()) { toast.error("Please enter your school."); return; }
    setSaving(true);
    try {
      await updateProfile({ display_name: trimmed, location, club_name: clubValue, school_name: school.trim(), share_consent: consent });
      toast.success("Profile complete — welcome to the Hub!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid="profile-gate">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative w-full max-w-lg my-6 bg-[#141414] border border-[#C41E24]/30 rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1"><Lock size={15} className="text-[#C41E24]" /><span className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold">Complete Your Profile</span></div>
        <h1 className="font-cover text-3xl sm:text-4xl uppercase tracking-tight">One quick step</h1>
        <p className="text-white/55 mt-2 text-sm">Add the player's name, club and school to unlock the Hub and get on the leaderboard. This shows even if the Blueprint was purchased under a parent's name.</p>

        <div className="mt-6 space-y-5">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2"><User size={13} className="text-[#C41E24]" /> Player Name <span className="text-[#C41E24]">*</span></label>
            <input data-testid="gate-name-input" type="text" value={name} maxLength={40} autoFocus
              onChange={(e) => setName(e.target.value)} placeholder="e.g. Leo Barker"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
            <div className="mt-2 flex items-center gap-2 text-sm text-white/60"><Trophy size={15} className="text-[#C41E24]" /> On the leaderboard: <span data-testid="gate-name-preview" className="font-bold text-white">{leaderboardPreview}</span></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2"><Shield size={13} className="text-[#C41E24]" /> Club <span className="text-[#C41E24]">*</span></label>
              <select data-testid="gate-club-select" value={clubChoice} onChange={(e) => setClubChoice(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]">
                <option value="">Select your club…</option>
                {CLUB_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="Other">Other (type your own)</option>
              </select>
              {clubChoice === "Other" && (
                <input data-testid="gate-club-custom" value={clubCustom} onChange={(e) => setClubCustom(e.target.value)}
                  placeholder="Type your club name" autoFocus
                  className="mt-2 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
              )}
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2"><GraduationCap size={13} className="text-[#C41E24]" /> School <span className="text-[#C41E24]">*</span></label>
              <input data-testid="gate-school-input" value={school} onChange={(e) => setSchool(e.target.value)}
                placeholder="e.g. Normal West HS"
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2"><MapPin size={13} className="text-[#C41E24]" /> Location <span className="text-white/30 normal-case font-normal">(optional)</span></label>
            <select data-testid="gate-location-select" value={location} onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]">
              <option value="">Select your area…</option>
              {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <p className="mt-2 text-xs text-white/40">Pick a Central Illinois town to appear on the <span className="text-[#C41E24] font-semibold">Central Illinois leaderboard</span>.</p>
          </div>


          <ShareConsentCheckbox testid="gate-consent" checked={consent} onChange={setConsent} />

          <button data-testid="gate-save-btn" onClick={save} disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider text-base px-6 py-4 rounded-lg transition-colors disabled:opacity-60">
            <Save size={18} /> {saving ? "Saving…" : "Save & Enter Hub"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
